import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Box, CircularProgress, Typography } from "@mui/material";
import { ACTIONS, PATHS } from "lib/routing";
import { mobileNavigate } from "lib/routing/navigate/navigate";
import { KioskQuestionsProps } from "./KioskQuestionnairePage.types";
import { actualiseAppointment } from "api/appointment/ActualiseAppontment/actualiseAppontment";
import ButtonsFooter from "lib/components/buttons/ButtonsFooter/ButtonsFooter";
import VerticalSpreadLayout from "lib/components/layout/VerticalSpreadLayout/VerticalSpreadLayout";
import ConfirmationModal from "lib/components/modals/ConfirmationModal/ConfirmationModal";
import { useGetKioskQuestionnaire } from "ui/appointment/hooks/useGetKioskQuestionnaire";
import { logEventToGoogleAnalytics } from "lib/util/GoogleAnalyticsUtil/logEvent";
import { EVENTS } from "lib/util/GoogleAnalyticsUtil/events";
import ErrorDisplay from "lib/components/error/ErrorDisplay/ErrorDisplay";
import { sxStyles } from "./KioskQuestionnairePage.styles";
import MapRawStringToReactElement from "lib/util/ReactComponentUtil/mapRawStringToReactElement/MapRawStringtoReactElement";
import { checkIn } from "api/appointment/CheckIn/checkIn";
import IMAGES from "lib/assets/images";
import { AxiosError } from "axios";
import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import {
  selectAppointmentMeta,
  selectApptDetail,
  selectKioskQuestionnaire,
} from "ui/appointment/ducks/selectors";
import { isNUP } from "lib/util/ValidatorUtil/isNupValidator/isNupValidator";
import { selectUser } from "lib/redux/user/selectors";
import { updateQueueForActualiseAppointment } from "ui/appointment/ducks";
import { handleErrorModal } from "lib/redux/notifications";
import {
  resetKioskQuestionnaireAnswers,
  setKioskCEQMSQuestionnaireAnswer,
  setKioskQuestionnaireAnswer,
  updateCheckInSuccessState,
} from "ui/appointment/ducks/appointmentsSlice";
import { KioskAnswer } from "api/appointment/ActualiseAppontment/actualiseAppontment.fromUi.types";
import CEQMSQuestion from "../CEQMSQuestion/CEQMSQuestion";
import {
  CEQMSQuestion as CEQMSQuestionType,
  Question,
} from "api/appointment/GetKioskQuestionnaire/getKioskQuestionnaire.Api.types";
import {
  AppointmentMeta,
  CEQMSQuestionsAnswers,
  KioskQuestionnaire,
  QuestionsAnswers,
} from "ui/appointment/ducks/appointments.types";
import { isEmpty } from "lodash-es";

// Constants
const TARGET_SYSTEM = {
  NGEMR: "NGEMR",
};

const TITLES = {
  REGISTRATION_SUCCESS: "Registration Successful",
  CHECKIN_SUCCESS: "Welcome to Our Clinic",
  CHECKIN_WARNING: "Oops...",
};

const KioskQuestionnairePage = ({
  isCheckIn,
  isFromMobile,
}: KioskQuestionsProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const classes = sxStyles();

  // Redux States
  const {
    logoCode,
    appointmentId,
    regInstitutionCode,
    institutionCode,
    targetSystem,
    clinicCode,
    actualizeStatus,
    institutionName,
    consultType,
    visitType,
    regAppointmentId,
    datetimeString: datetimeStringMeta,
    vcSupportStatus,
  }: AppointmentMeta = useAppSelector(selectAppointmentMeta);
  const {
    queueNumber = null,
    currentClinicCode = null,
    encounterId = null,
    appointmentTimeToArrive,
  } = useAppSelector(selectApptDetail) || {};
  const checkInClinicCode = currentClinicCode;
  const currentAppointmentTime = datetimeStringMeta || appointmentTimeToArrive;
  const { memberIdentifier } = useAppSelector(selectUser);
  const {
    questions,
    ceqmsQuestions,
    state,
    questionsAnswers,
    ceqmsQuestionsAnswers,
    isCEQMS,
    queueNumber: queueNumberFromKioskQuestionnaire,
    errorMessage,
  }: KioskQuestionnaire = useAppSelector(selectKioskQuestionnaire);
  const isVirtualConsultAppointment = vcSupportStatus === "supported";

  // Local States
  const [pageFlag, setPageFlag] = useState(false);
  const [hasCheckInError, setHasCheckInError] = useState(false);
  const [checkInErrorMsg, setCheckInErrorMsg] = useState<string | null>();
  const [hasActualiseError, setHasActualiseError] = useState(false);
  const [actualiseErrorMsg, setActualiseErrorMsg] = useState<string | null>();
  // States for Appointment Actualization
  const [isActualizing, setIsActualizing] = useState(false);
  // States for Check In
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  // States & Handlers for Confirmation Dialog, after Actualizing an appointment successfully
  const [confirmationTitle, setConfirmationTitle] = useState<string | null>();
  const [confirmationText, setConfirmationText] = useState<string | null>();
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const handleOpenConfirmation = () => setOpenConfirmation(true);

  // Handlers

  const handleCloseConfirmation = () => setOpenConfirmation(false);
  const handleErrorModalAction = (open: boolean, message: string | null) => {
    dispatch(handleErrorModal({ open, message }));
  };
  const handleSuccessModal = (
    checkInSuccessStatus: boolean,
    checkInSuccessTitleIcon: "checkIcon" | "errorIcon" | null,
    title: string | null,
    message: string | null,
  ) => {
    dispatch(
      updateCheckInSuccessState({
        checkInSuccessStatus,
        checkInSuccessTitleIcon,
        title,
        message,
      }),
    );
  };

  const handleQuestionnaireChange = (
    isChecked: boolean,
    answer: string,
    isRadio: boolean,
    questionId: string,
  ) => {
    if (isChecked && isRadio) {
      let answerMap: QuestionsAnswers = { [questionId]: answer === "t" };
      dispatch(setKioskQuestionnaireAnswer(answerMap));
    }
  };

  const handleCEQMSQuestionnaireChange = (
    isChecked: boolean,
    answer: string,
    isRadio: boolean,
    questionId: string,
  ) => {
    let questionAnswers =
      ceqmsQuestionsAnswers && ceqmsQuestionsAnswers[questionId]
        ? ceqmsQuestionsAnswers[questionId]
        : [];

    if (isChecked && isRadio) {
      let answerMap: CEQMSQuestionsAnswers = { [questionId]: [answer] };
      dispatch(setKioskCEQMSQuestionnaireAnswer(answerMap));
    } else if (isChecked) {
      let updated = [...questionAnswers, answer];
      let answerMap: CEQMSQuestionsAnswers = { [questionId]: updated };
      dispatch(setKioskCEQMSQuestionnaireAnswer(answerMap));
    } else {
      var index = questionAnswers.indexOf(answer);
      let updated = questionAnswers.toSpliced(index, 1);
      let answerMap: CEQMSQuestionsAnswers = { [questionId]: updated };
      dispatch(setKioskCEQMSQuestionnaireAnswer(answerMap));
    }
  };

  const onActualisedAppointment = (
    appointmentId: string,
    queueNumber: string,
    institutionName: string | null,
    consultType: string | null,
    encounterId: string | null,
    appointmentTime: string | null,
    logoCode: string | null,
  ) => {
    dispatch(
      updateQueueForActualiseAppointment({
        appointmentId,
        queueNumber,
        institutionName,
        consultType,
        encounterId,
        appointmentTime,
        logoCode,
      }),
    );
  };

  const shouldGetKioskQuestionnaire = useMemo(
    () =>
      shouldGetKioskQuestionnaireAccordingToTargetSystem(
        targetSystem,
        actualizeStatus,
        queueNumber,
      ),
    [actualizeStatus, queueNumber, targetSystem],
  );
  const shouldProceedActualize = useMemo(
    () =>
      shouldProceedActualizeAccordingToTargetSystem(
        targetSystem,
        actualizeStatus,
        queueNumber,
        encounterId,
        logoCode,
        institutionCode,
      ),
    [
      targetSystem,
      actualizeStatus,
      queueNumber,
      encounterId,
      logoCode,
      institutionCode,
    ],
  );
  const shouldProceedQueueStatusAndJourney = useMemo(
    () =>
      shouldProceedQueueStatusAndJourneyAccordingToTargetSystem(
        targetSystem,
        actualizeStatus,
        queueNumber,
        logoCode,
        institutionCode,
      ),
    [actualizeStatus, institutionCode, logoCode, queueNumber, targetSystem],
  );

  // Retrieve Kiosk Questionnaire from the remote API & Handle states for recorded Answers
  useGetKioskQuestionnaire({
    pageFlag,
    shouldGetKioskQuestionnaire: isCheckIn ? true : shouldGetKioskQuestionnaire,
    memberIdentifier,
    regInstitutionCode,
    institutionCode,
    targetSystem,
    clinicCode: isCheckIn ? checkInClinicCode : clinicCode,
    visitType,
    appointmentId,
    logoCode,
    isFromMobile,
  });

  // Send Appointment Registration Request
  // Actualise Appointment and Submit Questionnaire in the process
  const sendActualiseAppointmentRequest = async () => {
    if (isActualizing) return; // Prevent multiple simultaneous calls

    try {
      if (appointmentId && regInstitutionCode && regAppointmentId) {
        // Make the request
        setIsActualizing(true);

        const actualiseAppointmentPayload = await actualiseAppointment({
          MemberIdentifier: memberIdentifier,
          RegAppointmentId: regAppointmentId,
          RegInstitutionCode: regInstitutionCode,
          InstitutionCode: institutionCode,
          ClinicCode: clinicCode,
          QuestionnaireResponse: isCEQMS
            ? !isEmpty(ceqmsQuestionsAnswers)
              ? Object.entries(ceqmsQuestionsAnswers).map(([key, value]) => {
                  return {
                    QuestionId: key,
                    Response: null,
                    ResponseArray: value as string[],
                  } satisfies KioskAnswer;
                })
              : []
            : !isEmpty(questionsAnswers)
              ? Object.entries(questionsAnswers).map(([key, value]) => {
                  return {
                    QuestionId: key,
                    Response: value as boolean,
                    ResponseArray: null,
                  } satisfies KioskAnswer;
                })
              : vcSupportStatus?.toLocaleLowerCase() === "supported"
                ? !isEmpty(questions)
                  ? questions.map((question) => {
                      return {
                        QuestionId: question.QuestionId,
                        Response: false,
                        ResponseArray: null,
                      } satisfies KioskAnswer;
                    })
                  : []
                : [],
          VcSupportStatus: vcSupportStatus,
          IsCEQMS: isCEQMS,
        });

        // Update the redux store with the new queue number and encounterid, so that it can be used in the next step
        onActualisedAppointment(
          appointmentId,
          actualiseAppointmentPayload.QueueNumber,
          institutionName,
          consultType,
          actualiseAppointmentPayload.EncounterId,
          currentAppointmentTime,
          logoCode,
        );

        const hasNoQuestions = questions.length === 0;
        const hasNoCEQMSQuestions = isCEQMS && !ceqmsQuestions;
        const isNup = isNUP(institutionCode, logoCode);

        // redirect back to appointment landing page for virtual consult appointment
        if (isVirtualConsultAppointment) {
          navigate(PATHS.APPOINTMENT_MOBILE.path);
          return;
        }

        // redirect to queue chit journey page if there are no questions
        if (hasNoQuestions || hasNoCEQMSQuestions) {
          navigate(PATHS.APPOINTMENT_QUEUECHIT_JOURNEY_NO_BACK.path);
          return;
        }

        // Show registration successful popup for NUP
        if (isNup) {
          setConfirmationTitle(TITLES.REGISTRATION_SUCCESS);
          setConfirmationText(actualiseAppointmentPayload.ErrorText);
          handleOpenConfirmation();
          return;
        }
      }
    } catch (error) {
      // Default non-axios errors
      if (!(error instanceof AxiosError)) {
        handleErrorModalAction(true, null);
        return;
      }

      // Axios error with questions
      if (questions.length !== 0) {
        handleErrorModalAction(true, error.response?.data.Message);
        return;
      }

      // Axios error without questions
      setHasActualiseError(true);
      setActualiseErrorMsg(error.response?.data.Message);
    } finally {
      setIsActualizing(false);
    }
  };

  // Send CheckIn Request
  // CheckIn and Submit Questionnaire in the process
  const sendCheckInRequest = async () => {
    try {
      if (
        (isCEQMS && regAppointmentId && regInstitutionCode) ||
        (!isCEQMS &&
          regAppointmentId &&
          regInstitutionCode &&
          checkInClinicCode)
      ) {
        // Make the request
        setIsCheckingIn(true);

        const checkInPayload = await checkIn({
          MemberIdentifier: memberIdentifier,
          RegAppointmentId: regAppointmentId,
          EncounterId: encounterId,
          AppointmentTime: currentAppointmentTime,
          QuestionnaireResponse: isCEQMS
            ? ceqmsQuestionsAnswers
              ? Object.entries(ceqmsQuestionsAnswers).map(([key, value]) => {
                  return {
                    QuestionId: key,
                    Response: null,
                    ResponseArray: value as string[],
                  } satisfies KioskAnswer;
                })
              : []
            : questionsAnswers
              ? Object.entries(questionsAnswers).map(([key, value]) => {
                  return {
                    QuestionId: key,
                    Response: value as boolean,
                    ResponseArray: null,
                  } satisfies KioskAnswer;
                })
              : [],
          IsCEQMS: isCEQMS,
        });

        if (checkInPayload.EncounterStatus === "S") {
          handleSuccessModal(
            true,
            "checkIcon",
            TITLES.CHECKIN_SUCCESS,
            checkInPayload.Message,
          );
          navigate(PATHS.APPOINTMENT_QUEUECHIT_JOURNEY_NO_BACK.path);
        } else if (checkInPayload.EncounterStatus === "W") {
          handleSuccessModal(
            true,
            "errorIcon",
            TITLES.CHECKIN_WARNING,
            checkInPayload.ErrorText,
          );
          navigate(PATHS.APPOINTMENT_QUEUECHIT_JOURNEY_NO_BACK.path);
        }
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        if (questions.length === 0) {
          setCheckInErrorMsg(error.response?.data.Message);
          setHasCheckInError(true);
        } else {
          handleErrorModalAction(true, error.response?.data.Message);
        }
      } else {
        handleErrorModalAction(true, null);
      }
    } finally {
      setIsCheckingIn(false);
    }
  };

  useEffect(() => {
    logEventToGoogleAnalytics(EVENTS.VIEW_REGISTRATION_TRIAGE);

    dispatch(resetKioskQuestionnaireAnswers());
  }, [dispatch]);

  useEffect(() => {
    if (isCheckIn) {
      if (state === "success") {
        if (
          questions.length === 0 &&
          (ceqmsQuestions === null || ceqmsQuestions === undefined)
        ) {
          sendCheckInRequest();
        } else if (ceqmsQuestions?.General.length === 0) {
          if (ceqmsQuestions.Specialty.length > 0) {
            navigate(PATHS.APPOINTMENT_SPECIALTY_CHECKIN.path, {
              replace: true,
            });
          } else if (ceqmsQuestions.Payment.length > 0) {
            navigate(PATHS.APPOINTMENT_PAYMENT_CHECKIN.path, {
              replace: true,
            });
          }
        }
      }
    } else {
      // flow handling when getKioskQuestionnaire is not called
      if (!shouldGetKioskQuestionnaire) {
        if (shouldProceedActualize) {
          // Appt is not actualized
          sendActualiseAppointmentRequest();
        } else if (shouldProceedQueueStatusAndJourney) {
          // Appt is actualized

          onActualisedAppointment(
            appointmentId || "",
            queueNumber || "",
            institutionName,
            consultType,
            encounterId || "",
            currentAppointmentTime || "",
            logoCode,
          );
          navigate(PATHS.APPOINTMENT_QUEUECHIT_JOURNEY_NO_BACK.path);
        }
      }
      // normal flow handling after getKioskQuestionnaire is called
      else if (state === "success") {
        if (isVirtualConsultAppointment) {
          sendActualiseAppointmentRequest();
        } else if (queueNumberFromKioskQuestionnaire) {
          // If there is already the queue number return from getKioskQuestionnaire, just use the queue number and no calling of actualizeAppointment

          onActualisedAppointment(
            appointmentId || "",
            queueNumberFromKioskQuestionnaire,
            institutionName,
            consultType,
            encounterId || "",
            currentAppointmentTime || "",
            logoCode,
          );
          navigate(PATHS.APPOINTMENT_QUEUECHIT_JOURNEY_NO_BACK.path);
        } else if (isCEQMS) {
          if (ceqmsQuestions && ceqmsQuestions.General.length === 0) {
            // !exp General question may be empty, but Specialty or Payment questions may not be empty
            if (ceqmsQuestions.Specialty.length > 0) {
              navigate(PATHS.APPOINTMENT_REGISTER_SPECIALTY_KIOSK.path, {
                replace: true,
              });
            } else if (ceqmsQuestions.Payment.length > 0) {
              navigate(PATHS.APPOINTMENT_REGISTER_PAYMENT_KIOSK.path, {
                replace: true,
              });
            }
          } else if (!ceqmsQuestions) {
            // isCEQMS true, ceqmsQuestions null
            sendActualiseAppointmentRequest();
          }
        }
        // register the appointment directly if there is no kiosk question
        else if (questions.length === 0) {
          sendActualiseAppointmentRequest();
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, ceqmsQuestions, state, queueNumberFromKioskQuestionnaire]);

  return (
    <VerticalSpreadLayout>
      {state === "loading" || isActualizing || isCheckingIn ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: 4,
          }}
        >
          <CircularProgress />
        </Box>
      ) : state === "error" ? (
        <ErrorDisplay
          errorMessage={errorMessage}
          onTryAgain={() => setPageFlag(!pageFlag)}
        />
      ) : hasActualiseError ? (
        <ErrorDisplay
          errorMessage={actualiseErrorMsg}
          onTryAgain={() => {
            sendActualiseAppointmentRequest();
            setHasActualiseError(false);
          }}
        />
      ) : hasCheckInError ? (
        <ErrorDisplay
          errorMessage={checkInErrorMsg}
          onTryAgain={() => {
            sendCheckInRequest();
            setHasCheckInError(false);
          }}
        />
      ) : !isVirtualConsultAppointment ? (
        <>
          <Box sx={{ px: 2, pt: 1, mb: 4 }}>
            {!isCEQMS && questions.length > 0 ? (
              // Render only for EQMS Questionnaire
              <>
                <Box sx={{ mt: 3 }}>
                  <Box sx={classes.requireBox}>
                    <Box
                      component={"img"}
                      src={IMAGES.general.InformationErrorIcon}
                    />
                    <Typography component={"span"} sx={classes.requireText}>
                      All fields required
                    </Typography>
                  </Box>
                </Box>
                {questions?.map((question: Question, i: number | undefined) => {
                  return (
                    <Box key={question.QuestionId}>
                      <CEQMSQuestion
                        questionId={question.QuestionId}
                        questionNumber={i}
                        questionText={question.QuestionEnglish}
                        choices={["Yes", "No"]}
                        questionType={"EQMS"}
                        handleQuestionnaireChange={(
                          isChecked,
                          answer,
                          isRadio,
                        ) =>
                          handleQuestionnaireChange(
                            isChecked,
                            answer,
                            isRadio,
                            question.QuestionId,
                          )
                        }
                        required={true}
                      />
                    </Box>
                  );
                })}
              </>
            ) : null}

            {isCEQMS &&
            ceqmsQuestions?.General &&
            ceqmsQuestions.General.length > 0 ? (
              <>
                {ceqmsQuestions.General?.map((question, i) => {
                  return (
                    <Box>
                      <CEQMSQuestion
                        questionNumber={i}
                        questionId={question.QuestionId}
                        questionText={question.QuestionEnglish}
                        choices={question.Choices}
                        questionType={question.QuestionType}
                        handleQuestionnaireChange={(
                          isChecked,
                          answer,
                          isRadio,
                        ) =>
                          handleCEQMSQuestionnaireChange(
                            isChecked,
                            answer,
                            isRadio,
                            question.QuestionId,
                          )
                        }
                        required={question.Required}
                      />
                    </Box>
                  );
                })}
              </>
            ) : null}
          </Box>

          <Box sx={{ px: 2, pb: 2.5 }}>
            <ButtonsFooter
              nextButtonText="Next"
              cancelButtonText="Back"
              isDisabled={
                isActualizing ||
                isCheckingIn ||
                !areAllQuestionsAnswered(
                  ceqmsQuestions?.General ?? null,
                  questions,
                  questionsAnswers,
                  ceqmsQuestionsAnswers,
                  isCEQMS,
                )
              }
              onClickNext={() => {
                if (isCheckIn) {
                  if (isCEQMS) {
                    if (ceqmsQuestions) {
                      if (ceqmsQuestions.Specialty.length > 0) {
                        navigate(PATHS.APPOINTMENT_SPECIALTY_CHECKIN.path, {
                          replace: true,
                        });
                      } else if (ceqmsQuestions.Payment.length > 0) {
                        navigate(PATHS.APPOINTMENT_PAYMENT_CHECKIN.path, {
                          replace: true,
                        });
                      } else {
                        sendCheckInRequest();
                      }
                    }
                  } else {
                    sendCheckInRequest();
                  }
                } else if (encounterId) {
                  navigate(PATHS.APPOINTMENT_QUEUECHIT_JOURNEY_NO_BACK.path);
                } else if (!isCheckIn && isCEQMS) {
                  if (ceqmsQuestions) {
                    if (ceqmsQuestions.Specialty.length > 0) {
                      navigate(
                        PATHS.APPOINTMENT_REGISTER_SPECIALTY_KIOSK.path,
                        {
                          replace: true,
                        },
                      );
                    } else if (ceqmsQuestions.Payment.length > 0) {
                      navigate(PATHS.APPOINTMENT_REGISTER_PAYMENT_KIOSK.path, {
                        replace: true,
                      });
                    }
                  } else {
                    sendActualiseAppointmentRequest();
                  }
                } else {
                  // EQMS actualise flow
                  sendActualiseAppointmentRequest();
                }
              }}
              onClickCancel={() => {
                if (
                  location.pathname ===
                  PATHS.APPOINTMENT_REGISTER_KIOSK_MOBILE.path
                ) {
                  mobileNavigate(ACTIONS.MOBILE_DASHBOARD_PATH);
                } else {
                  navigate(-1);
                }
              }}
            />
          </Box>

          {/* After Successful Actualization */}
          <ConfirmationModal
            open={openConfirmation}
            hideCancelButton={true}
            title={confirmationTitle || ""}
            body={MapRawStringToReactElement(confirmationText || "")}
            nextButtonText="OK"
            onClickNext={() => {
              // proceed next after successful actualisation
              navigate(PATHS.APPOINTMENT_QUEUECHIT_JOURNEY_NO_BACK.path);
            }}
            onClose={handleCloseConfirmation}
          />
        </>
      ) : null}
    </VerticalSpreadLayout>
  );
};

/**
 * If the target system is NGEMR, and the actualize status is P and there is no queue number, then
 * return true. Otherwise, if there is no queue number, return true. Otherwise, return false
 * @param {string | null} targetSystem - The system that the patient is registered in.
 * @param {string | null} actualizeStatus - This is the status of the patient's actualization.
 * @param {string | null} queueNumber - The queue number of the patient.
 * @returns A boolean value
 */
const shouldGetKioskQuestionnaireAccordingToTargetSystem = (
  targetSystem: string | null,
  actualizeStatus: string | null,
  queueNumber: string | null,
) => {
  if (targetSystem === TARGET_SYSTEM.NGEMR) {
    if (actualizeStatus === "P" && !queueNumber) {
      return true;
    } else {
      return false;
    }
  } else if (!queueNumber) {
    return true;
  } else {
    return false;
  }
};

/**
 * If the target system is NGEMR and the actualize status is P and the queue number is not null, then
 * return true. Otherwise, return false
 * @param {string | null} targetSystem - string | null
 * @param {string | null} actualizeStatus - The status of the actualization process.
 * @param {string | null} queueNumber - The queue number of the patient in the target system.
 * @returns A boolean value
 */
const shouldProceedActualizeAccordingToTargetSystem = (
  targetSystem: string | null,
  actualizeStatus: string | null,
  queueNumber: string | null,
  encounterId: string | null,
  logoCode: string | null,
  institutionCode: string | null,
) => {
  if (targetSystem === TARGET_SYSTEM.NGEMR) {
    // NUP flow
    if (isNUP(institutionCode, logoCode)) {
      if (actualizeStatus === "P" && !encounterId) {
        return true;
      } else {
        return false;
      }
    } else {
      // SOC flow
      if (actualizeStatus === "P" && queueNumber) {
        // SOC flow
        return true;
      } else {
        return false;
      }
    }
  } else {
    return false;
  }

  // !exp: original logic
  // if (targetSystem === TARGET_SYSTEM.NGEMR) {
  //   if (actualizeStatus === "P" && queueNumber) {
  //     return true;
  //   } else {
  //     return false;
  //   }
  // } else {
  //   return false;
  // }
};

/**
 * It returns true if the target system is NGEMR and the actualize status is W, S, or E and the queue
 * number is not null
 * @param {string | null} targetSystem - string | null
 * @param {string | null} actualizeStatus - string | null
 * @param {string | null} queueNumber - The queue number of the patient
 * @returns A boolean value
 */
const shouldProceedQueueStatusAndJourneyAccordingToTargetSystem = (
  targetSystem: string | null,
  actualizeStatus: string | null,
  queueNumber: string | null,
  logoCode: string | null,
  institutionCode: string | null,
) => {
  if (targetSystem === "NGEMR") {
    // NUP flow
    if (isNUP(institutionCode, logoCode)) {
      if (queueNumber) {
        return true;
      } else {
        return false;
      }
    } else {
      // SOC flow
      if (
        (actualizeStatus === "W" ||
          actualizeStatus === "S" ||
          actualizeStatus === "E") &&
        queueNumber
      ) {
        return true;
      } else {
        return false;
      }
    }
  } else if (queueNumber) {
    return true;
  } else {
    return false;
  }
};

/**
 * Determines if all kiosk questions have been answered. We should disable the
 * NEXT button to prevent user from actualizing this appointment, if not all
 * questions are answered.
 *
 * @param {string[] | null} ansList  List of answers for the kiosk questionnaire
 * @returns {boolean}  True if all answered, False otherwise.
 */
const areAllQuestionsAnswered = (
  ceqmsQuestions: CEQMSQuestionType[] | null,
  eqmsQuestions: Question[],
  eqmsAns?: QuestionsAnswers,
  ceqmsQuestionsAnswers?: CEQMSQuestionsAnswers,
  isCEQMS: boolean = false,
) => {
  if (isCEQMS) {
    if (ceqmsQuestions && ceqmsQuestions.length > 0) {
      if (ceqmsQuestionsAnswers) {
        // at least 1 answer hasn't been answered yet
        for (let i = 0; i < ceqmsQuestions.length; i++) {
          const question = ceqmsQuestions[i];
          if (
            question.Required &&
            (!ceqmsQuestionsAnswers[question.QuestionId] ||
              ceqmsQuestionsAnswers[question.QuestionId].length < 1)
          ) {
            return false;
          }
        }
      } else {
        return false;
      }

      return true;
    } else {
      return true;
    }
  } else {
    if (eqmsQuestions.length > 0) {
      return Object.keys(eqmsAns || {}).length === eqmsQuestions.length;
    } else {
      return true;
    }
  }
};

export default KioskQuestionnairePage;

export const exportForTesting = {
  shouldGetKioskQuestionnaireAccordingToTargetSystem,
  shouldProceedActualizeAccordingToTargetSystem,
  shouldProceedQueueStatusAndJourneyAccordingToTargetSystem,
  areAllQuestionsAnswered,
};
